import { motion } from "framer-motion";
import { Globe, HandHeart, Recycle, Certificate } from "@phosphor-icons/react";
import { FARM_IMAGE } from "@/data/botanicals";

const EASE = [0.16, 1, 0.3, 1];

const REGIONS = [
  { place: "Kannauj, India", herbs: "Rose Petals — the perfume capital of the East" },
  { place: "Kerala, India", herbs: "Tulsi, Brahmi & Ashwagandha — certified organic farms" },
  { place: "Provence, France", herbs: "Lavender — high-altitude aromatic fields" },
  { place: "Nile Delta, Egypt", herbs: "Blue Lotus — heritage water gardens" },
  { place: "Pacific Northwest, USA", herbs: "Mullein & Mugwort — wild-tended meadows" },
  { place: "Nile Valley & Anatolia", herbs: "Chamomile & Peppermint — smallholder cooperatives" },
];

const PILLARS = [
  { icon: Globe, title: "Global Organic Sourcing", body: "Every botanical is traceable to a certified-organic farm or wild-tending cooperative across three continents." },
  { icon: HandHeart, title: "Fair-Trade First", body: "We pay 30–60% above market rates and sign multi-year contracts so our growers can farm regeneratively." },
  { icon: Recycle, title: "Zero-Plastic Packaging", body: "FSC-certified paper, soy inks and biodegradable filters. The pack composts; the ritual remains." },
  { icon: Certificate, title: "Third-Party Verified", body: "GMP manufacturing, organic certification and per-batch lab reports available to every partner." },
];

const Sustainability = () => (
  <section id="sourcing" data-testid="sustainability-section" className="relative bg-[#E0E5D3] py-28 lg:py-40 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-6"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#0B3B24]/60">Sustainability & Ethics</span>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl text-[#0B3B24] tracking-tight leading-tight">
            Grown with reverence, <span className="italic">traded with honour.</span>
          </h2>
          <p className="mt-6 text-[#0B3B24]/70 text-base leading-relaxed font-light max-w-xl">
            Ayurveda teaches that a plant's medicine reflects how it was grown. Our supply chain
            is built on that principle — organic soil, fair hands, and total transparency from
            field to filter.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="sustainability-pillars">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                className="rounded-xl bg-[#F5F1E8] border border-[#0B3B24]/10 p-6"
              >
                <p.icon size={28} weight="duotone" className="text-[#C5A880]" />
                <h3 className="mt-4 font-display text-xl text-[#0B3B24]">{p.title}</h3>
                <p className="mt-2 text-sm text-[#0B3B24]/65 leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="lg:col-span-6"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#0B3B24]/20">
            <img src={FARM_IMAGE} alt="Organic herb farm at golden hour" className="w-full h-[320px] lg:h-[380px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B3B24]/60 to-transparent" />
            <span className="absolute bottom-5 left-6 font-display italic text-xl text-[#F5F1E8]">
              Harvest at first light — Kerala, India
            </span>
          </div>

          <div className="mt-8 rounded-2xl bg-[#0B3B24] p-8 lg:p-10" data-testid="sourcing-regions">
            <span className="text-xs uppercase tracking-[0.25em] text-[#C5A880]">The Sourcing Atlas</span>
            <div className="mt-6 space-y-0">
              {REGIONS.map((r, i) => (
                <motion.div
                  key={r.place}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-3.5 border-b border-[#F5F1E8]/10 last:border-0"
                >
                  <span className="font-display text-lg text-[#F5F1E8] whitespace-nowrap">{r.place}</span>
                  <span className="text-sm text-[#F5F1E8]/55 font-light">{r.herbs}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

export default Sustainability;
